import React, { useState, useEffect, useMemo } from 'react';
import {
  ColumnLayout,
  Container,
  Form,
  FormField,
  Header,
  Select,
  SpaceBetween,
  Alert,
} from '@cloudscape-design/components';
import { DownloadButton } from './download';
import { useGetGRCReviewCycleSnapshotUrl } from 'src/api/hooks/useControlData';
import type { GetSnapshotCommandInput } from '@amzn/controllershiphub-typescript-client';

enum GRCReviewCyclePhase {
  MONTHLY = 'monthly',
  QUARTERLY = 'quarterly',
}

// Constants
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const QUARTERLY_MONTHS = ['March', 'June', 'September', 'December'];
const START_YEAR = 2025;
const AVAILABILITY_DAY_THRESHOLD = 15;

// Business logic helpers
const getAvailableMonths = (
  year: string,
  currentYear: number,
  currentMonthIndex: number,
  currentDay: number
) => {
  if (year === '2025') {
    // Starting year will have limited months
    const startIndex = MONTHS.indexOf('October');
    const endIndex =
      year === `${currentYear}` && currentDay >= AVAILABILITY_DAY_THRESHOLD
        ? currentMonthIndex + 1
        : currentMonthIndex;
    return year === `${currentYear}`
      ? MONTHS.slice(startIndex, endIndex)
      : MONTHS.slice(startIndex);
  } else if (year !== `${currentYear}`) {
    // Previous years will have all months as options
    return MONTHS;
  }
  // Current year will have limited months
  return currentDay >= AVAILABILITY_DAY_THRESHOLD
    ? MONTHS.slice(0, currentMonthIndex + 1)
    : MONTHS.slice(0, currentMonthIndex);
};

const getPhaseOptions = (month: string, currentMonthIndex: number, currentDay: number) => {
  const options = [{ label: 'Monthly', value: GRCReviewCyclePhase.MONTHLY }];

  const isQuarterlyMonth =
    QUARTERLY_MONTHS.includes(month) &&
    currentDay >= 1 &&
    (currentMonthIndex >= 3 ||
      currentMonthIndex >= 6 ||
      currentMonthIndex >= 9 ||
      currentMonthIndex >= 0);

  if (isQuarterlyMonth) {
    options.push({ label: 'Quarterly', value: GRCReviewCyclePhase.QUARTERLY });
  }

  return options;
};

const monthToNumber = (monthName: string): string => {
  const monthIndex = MONTHS.findIndex((m) => m.toLowerCase() === monthName.toLowerCase());
  return monthIndex !== -1 ? (monthIndex + 1).toString() : monthName;
};

export function ReportsSnapshotsForm() {
  const [error, setError] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [year, setYear] = useState<string>('');
  const [month, setMonth] = useState<string>('');
  const [grcReviewCyclePhase, setGRCReviewCyclePhase] = useState<GRCReviewCyclePhase | undefined>(
    undefined
  );

  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonthIndex = currentDate.getMonth();
  const currentDay = currentDate.getDate();

  const years = Array.from({ length: currentYear - START_YEAR + 1 }, (_, index) =>
    (START_YEAR + index).toString()
  );

  const availableMonths = useMemo(() => {
    if (!year) return [];
    return getAvailableMonths(year, currentYear, currentMonthIndex, currentDay).map((m) => ({
      label: m,
      value: m,
    }));
  }, [year, currentYear, currentMonthIndex, currentDay]);

  const phaseOptions = useMemo(() => {
    if (!month) return [];
    return getPhaseOptions(month, currentMonthIndex, currentDay);
  }, [month, currentMonthIndex, currentDay]);

  const snapshotInput = useMemo(
    (): GetSnapshotCommandInput => ({
      controlId: 'GRC5253',
      parameters: [
        { key: 'phasename', value: grcReviewCyclePhase },
        { key: 'month', value: monthToNumber(month) },
        { key: 'year', value: year },
      ],
    }),
    [grcReviewCyclePhase, month, year]
  );

  const {
    data: snapshotData,
    refetch,
    isLoading,
    error: queryError,
  } = useGetGRCReviewCycleSnapshotUrl(snapshotInput, false);

  const isDownloadButtonDisabled = !(year && month && grcReviewCyclePhase);

  const handleYearChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setYear(detail.selectedOption.value);
      setMonth('');
      setGRCReviewCyclePhase(undefined);
    }
  };

  const handleMonthChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setMonth(detail.selectedOption.value);
      setGRCReviewCyclePhase(undefined);
    }
  };

  const handlePhaseChange = ({ detail }: any) => {
    if (detail.selectedOption.value) {
      setGRCReviewCyclePhase(detail.selectedOption.value as GRCReviewCyclePhase);
    }
  };

  return (
    <Container header={<Header variant="h2">GRC Review Cycle Snapshot</Header>}>
      <Form
        errorText={error ? 'There was an error generating your snapshot.' : undefined}
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <DownloadButton
              getUrlFunction={() => {
                setError(false);
                setSuccess(false);
                return refetch()
                  .then((result) => {
                    if (result.data) {
                      setSuccess(true);
                    }
                    return result.data;
                  })
                  .catch((err) => {
                    setError(true);
                    throw err;
                  });
              }}
              setError={setError}
              getUrlFunctionProps={{}}
              buttonContent={isLoading ? 'Generating...' : 'Download Snapshot'}
              urlPath="presignedUrl"
              buttonDisabled={isDownloadButtonDisabled || isLoading}
            />
          </SpaceBetween>
        }
      >
        <SpaceBetween size="xl">
          <ColumnLayout columns={3}>
            <FormField label="Year">
              <Select
                placeholder="Select Year"
                selectedOption={year ? { label: year, value: year } : null}
                onChange={handleYearChange}
                options={years.map((y) => ({ label: y, value: y }))}
              />
            </FormField>

            <FormField label="Month">
              <Select
                placeholder="Select Month"
                selectedOption={month ? { label: month, value: month } : null}
                onChange={handleMonthChange}
                options={availableMonths}
                disabled={!year}
              />
            </FormField>

            <FormField label="Phase">
              <Select
                placeholder="Select Phase"
                selectedOption={
                  grcReviewCyclePhase
                    ? {
                        label:
                          grcReviewCyclePhase.charAt(0).toUpperCase() +
                          grcReviewCyclePhase.slice(1),
                        value: grcReviewCyclePhase,
                      }
                    : null
                }
                onChange={handlePhaseChange}
                options={phaseOptions}
                disabled={!month}
              />
            </FormField>
          </ColumnLayout>

          {success && (
            <Alert
              dismissAriaLabel="Close alert"
              type="success"
              header="Success"
              onDismiss={() => setSuccess(false)}
            >
              Snapshot generated successfully and download started.
            </Alert>
          )}

          {(error || queryError) && (
            <Alert
              dismissAriaLabel="Close alert"
              type="error"
              header="Error"
              onDismiss={() => setError(false)}
            >
              {queryError?.message || 'There was an error generating your snapshot.'}
            </Alert>
          )}
        </SpaceBetween>
      </Form>
    </Container>
  );
}
